import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCar, FaKey, FaUserTie, FaMoon, FaSun, FaPhone, FaEnvelope, FaMapMarkerAlt, FaArrowRight, FaCheck, FaCarSide, FaClock, FaChevronLeft, FaChevronRight, FaWhatsapp, FaArrowUp, FaSearch, FaTimes } from 'react-icons/fa';
import './App.css';

const slideshowImages = [
  { url: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=1920', title: 'Rolls-Royce Phantom', subtitle: 'Ultimate Luxury' },
  { url: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=1920', title: 'Bentley Continental GT', subtitle: 'Performance Meets Elegance' },
  { url: 'https://images.unsplash.com/photo-1619682817481-e994891cd1f5?w=1920', title: 'Mercedes-Benz S-Class', subtitle: 'The Pinnacle of Comfort' },
  { url: 'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?w=1920', title: 'Porsche 911 Carrera', subtitle: 'Pure Driving Dynamics' },
  { url: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=1920', title: 'Lamborghini Huracán', subtitle: 'Exhilarating Power' },
  { url: 'https://images.unsplash.com/photo-1594855557608-05c9a489cf26?w=1920', title: 'Ferrari F8 Tributo', subtitle: 'Italian Excellence' }
];

const carData = [
  {
    id: 1,
    name: 'Rolls-Royce Phantom',
    image: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=800',
    price: 2100,
    features: ['6.75L V12 Engine', 'Automatic Transmission', 'Premium Sound', 'Leather Interior']
  },
  {
    id: 2,
    name: 'Bentley Continental GT',
    image: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=800',
    price: 1680,
    features: ['4.0L V8 Engine', 'All-Wheel Drive', 'Naim Audio', 'Heated Seats']
  },
  {
    id: 3,
    name: 'Mercedes-Benz S-Class',
    image: 'https://images.unsplash.com/photo-1619682817481-e994891cd1f5?w=800',
    price: 1120,
    features: ['3.0L Inline-6 Engine', '9G-TRONIC Transmission', 'MBUX System', 'Massage Seats']
  },
  {
    id: 4,
    name: 'Porsche 911 Carrera',
    image: 'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?w=800',
    price: 1330,
    features: ['3.0L Twin-Turbo Flat-6', 'PDK Transmission', 'Sport Chrono', 'Carbon Ceramic Brakes']
  },
  {
    id: 5,
    name: 'Lamborghini Huracán',
    image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=800',
    price: 2800,
    features: ['5.2L V10 Engine', '7-Speed DCT', 'Carbon Fiber Interior', 'Aero Kit']
  },
  {
    id: 6,
    name: 'Ferrari F8 Tributo',
    image: 'https://images.unsplash.com/photo-1594855557608-05c9a489cf26?w=800',
    price: 3080,
    features: ['3.9L V8 Engine', '7-Speed F1 DCT', 'Carbon Fiber Seats', 'Sport Exhaust']
  }
];

const leasingCars = [
  { id: 1, name: 'BMW 7 Series', monthly: 1258, duration: '36 months', mileage: '10,000 mi/year' },
  { id: 2, name: 'Audi A8', monthly: 1398, duration: '36 months', mileage: '12,000 mi/year' },
  { id: 3, name: 'Mercedes E-Class', monthly: 1048, duration: '48 months', mileage: '15,000 mi/year' },
  { id: 4, name: 'Range Rover Sport', monthly: 1258, duration: '36 months', mileage: '12,000 mi/year' },
];

const services = [
  { icon: <FaCar />, title: 'Luxury Car Rental', description: 'Experience the thrill of driving premium vehicles for any occasion', features: ['Daily & Weekly Rentals', 'Wide Fleet Selection', 'Delivery & Collection', '24/7 Support'] },
  { icon: <FaKey />, title: 'Flexible Leasing', description: 'Drive your dream car with our hassle-free leasing options', features: ['Custom Terms', 'Maintenance Included', 'Upgrade Options', 'No Hidden Fees'] },
  { icon: <FaUserTie />, title: 'Chauffeur Service', description: 'Professional drivers for business, events, and special occasions', features: ['Punctual & Reliable', 'Trained Professionals', 'Luxury Vehicles', 'Airport Transfers'] }
];

function App() {
  const [darkMode, setDarkMode] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', date: '', message: '' });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    document.body.className = darkMode ? 'dark-mode' : 'light-mode';
  }, [darkMode]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setSearchResults([]);
      return;
    }

    const allCars = [...carData];
    const results = allCars.filter(car => 
      car.name.toLowerCase().includes(query.toLowerCase()) ||
      car.features.some(f => f.toLowerCase().includes(query.toLowerCase()))
    );
    setSearchResults(results);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your inquiry! We will contact you shortly.');
    setFormData({ name: '', email: '', phone: '', service: '', date: '', message: '' });
  };

  return (
    <div className={`app ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      <div className="top-header">
        <div className="top-header-content">
          <div className="top-header-left">
            <span><FaPhone /> +267 71 123 4567</span>
            <span><FaEnvelope /> info@luxuryleasing.co.bw</span>
          </div>
          <div className="top-header-right">
            <span>Gaborone, Botswana</span>
          </div>
        </div>
      </div>
      <motion.nav className="navbar" initial={{ y: -100 }} animate={{ y: 0 }}>
        <div className="nav-brand"><FaCar className="brand-icon" /><span>LUXURY LEASING</span></div>
        <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
          <a href="#home">Home</a><a href="#fleet">Fleet</a><a href="#leasing">Leasing</a><a href="#chauffeur">Chauffeur</a><a href="#contact">Contact</a>
        </div>
        <div className="nav-actions">
          <motion.button className="search-toggle" onClick={() => setIsSearchOpen(true)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}><FaSearch /></motion.button>
          <motion.button className="theme-toggle" onClick={() => setDarkMode(!darkMode)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>{darkMode ? <FaSun /> : <FaMoon />}</motion.button>
          <motion.button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)} whileTap={{ scale: 0.95 }}><span></span><span></span><span></span></motion.button>
        </div>
      </motion.nav>

      <section id="home" className="hero">
        <div className="slideshow-container">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              className="slide"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
              style={{ backgroundImage: `url(${slideshowImages[currentSlide].url})` }}
            >
              <div className="slide-overlay"></div>
            </motion.div>
          </AnimatePresence>
          
          <div className="slideshow-controls">
            <button className="slide-btn prev" onClick={() => setCurrentSlide((prev) => (prev - 1 + slideshowImages.length) % slideshowImages.length)}>
              <FaChevronLeft />
            </button>
            <div className="slide-indicators">
              {slideshowImages.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
            <button className="slide-btn next" onClick={() => setCurrentSlide((prev) => (prev + 1) % slideshowImages.length)}>
              <FaChevronRight />
            </button>
          </div>
        </div>
        
        <motion.div className="hero-content" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
          <motion.h1 initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>Experience Luxury on Wheels</motion.h1>
          <motion.p initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>Premium car rental, flexible leasing, and professional chauffeur services</motion.p>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              className="slideshow-text"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <motion.h3
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {slideshowImages[currentSlide].title}
              </motion.h3>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {slideshowImages[currentSlide].subtitle}
              </motion.p>
            </motion.div>
          </AnimatePresence>
          <motion.div className="hero-buttons" initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
            <motion.a href="#fleet" className="btn btn-primary" whileHover={{ scale: 1.05 }}>Explore Fleet <FaArrowRight /></motion.a>
            <motion.a href="#contact" className="btn btn-secondary" whileHover={{ scale: 1.05 }}>Book Now</motion.a>
          </motion.div>
        </motion.div>
        <motion.div className="scroll-indicator" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <div className="mouse-icon">
            <div className="mouse-wheel"></div>
          </div>
        </motion.div>
      </section>

      <section className="services">
        <motion.div className="section-header" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}><h2>Our Premium Services</h2><p>Discover the ultimate luxury automotive experience</p></motion.div>
        <div className="services-grid">
          {services.map((service, index) => (
            <motion.div key={index} className="service-card" initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} whileHover={{ y: -10 }}>
              <motion.div className="service-icon" whileHover={{ rotate: 360, scale: 1.1 }}>{service.icon}</motion.div>
              <h3>{service.title}</h3><p>{service.description}</p>
              <ul>{service.features.map((f, i) => <li key={i}><FaCheck /> {f}</li>)}</ul>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="fleet" className="fleet">
        <motion.div className="section-header" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}><h2>Our Premium Fleet</h2><p>Choose from our collection of world-class luxury vehicles</p></motion.div>
        <div className="fleet-grid">
          {carData.map((car) => (
            <motion.div key={car.id} className="car-card" initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} whileHover={{ y: -10 }}>
              <div className="car-image"><img src={car.image} alt={car.name} /><div className="car-overlay"><motion.button className="view-details-btn" whileHover={{ scale: 1.1 }}>View Details</motion.button></div></div>
              <div className="car-info"><h3>{car.name}</h3><div className="car-price"><span className="price">{car.price} P</span><span className="period">/day</span></div><div className="car-features">{car.features.slice(0, 2).map((f, i) => <span key={i} className="feature-tag">{f}</span>)}</div><motion.button className="rent-btn" whileHover={{ scale: 1.02 }}>Rent Now</motion.button></div>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="leasing" className="leasing">
        <motion.div className="section-header" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}><h2>Flexible Leasing Plans</h2><p>Drive your dream car with our customizable lease options</p></motion.div>
        <div className="leasing-grid">
          {leasingCars.map((car) => (
            <motion.div key={car.id} className="leasing-card" initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} whileHover={{ scale: 1.03 }}>
              <motion.div className="leasing-badge" animate={{ rotate: [0, 5, -5, 0] }} transition={{ repeat: Infinity, duration: 3 }}>Popular</motion.div>
              <h3>{car.name}</h3><div className="leasing-price"><span className="amount">{car.monthly} P</span><span className="period">/month</span></div>
              <div className="leasing-details"><div className="detail-item"><FaClock /> <span>{car.duration}</span></div><div className="detail-item"><FaCarSide /> <span>{car.mileage}</span></div></div>
              <ul className="leasing-features"><li><FaCheck /> Full Insurance</li><li><FaCheck /> Maintenance Included</li><li><FaCheck /> Roadside Assistance</li><li><FaCheck /> 24/7 Support</li></ul>
              <motion.button className="lease-btn" whileHover={{ scale: 1.05 }}>Start Leasing</motion.button>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="chauffeur" className="chauffeur">
        <div className="chauffeur-content">
          <motion.div className="chauffeur-text" initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }}>
            <h2>Professional Chauffeur Service</h2><p>Experience the pinnacle of luxury travel with our professionally trained chauffeurs</p>
            <div className="chauffeur-features">
              {[{ icon: <FaUserTie />, title: 'Expert Chauffeurs', desc: 'Highly trained, vetted professionals' }, { icon: <FaClock />, title: 'Punctual Service', desc: 'Always on time, every time' }, { icon: <FaCar />, title: 'Premium Vehicles', desc: 'Latest luxury models' }, { icon: <FaMapMarkerAlt />, title: 'Any Destination', desc: 'City or countryside' }].map((f, i) => (
                <motion.div key={i} className="chauffeur-feature" whileHover={{ x: 10 }}><div className="feature-icon">{f.icon}</div><div className="feature-text"><h4>{f.title}</h4><p>{f.desc}</p></div></motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div className="chauffeur-image" initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }}>
            <img src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800" alt="Chauffeur Service" />
            <motion.div className="chauffeur-stats" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <div className="stat"><span className="stat-number">500+</span><span className="stat-label">Chauffeurs</span></div>
              <div className="stat"><span className="stat-number">50k+</span><span className="stat-label">Happy Clients</span></div>
              <div className="stat"><span className="stat-number">99%</span><span className="stat-label">Satisfaction</span></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="contact" className="contact">
        <motion.div className="section-header" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}><h2>Book Your Experience</h2><p>Get in touch with our luxury concierge team</p></motion.div>
        <motion.div className="contact-container" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}>
          <div className="contact-info">
            <h3>Contact Information</h3>
            <div className="info-items">
              <motion.div className="info-item" whileHover={{ x: 5 }}><FaPhone className="info-icon" /><div><h4>Phone</h4><p>+1 (800) LUXURY-1</p></div></motion.div>
              <motion.div className="info-item" whileHover={{ x: 5 }}><FaEnvelope className="info-icon" /><div><h4>Email</h4><p>concierge@luxurymotors.com</p></div></motion.div>
              <motion.div className="info-item" whileHover={{ x: 5 }}><FaMapMarkerAlt className="info-icon" /><div><h4>Location</h4><p>123 Luxury Lane, Beverly Hills, CA</p></div></motion.div>
            </div>
            <div className="working-hours"><h4>Working Hours</h4><p>Monday - Sunday: 24/7</p><p>Airport Services: Always Available</p></div>
          </div>
          <form className="contact-form" onSubmit={handleSubmit}>
            <motion.div className="form-group" whileFocus={{ scale: 1.02 }}><input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleInputChange} required /></motion.div>
            <motion.div className="form-group" whileFocus={{ scale: 1.02 }}><input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleInputChange} required /></motion.div>
            <motion.div className="form-group" whileFocus={{ scale: 1.02 }}><input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleInputChange} required /></motion.div>
            <motion.div className="form-group" whileFocus={{ scale: 1.02 }}><select name="service" value={formData.service} onChange={handleInputChange} required><option value="">Select Service</option><option value="rental">Car Rental</option><option value="leasing">Leasing</option><option value="chauffeur">Chauffeur Service</option></select></motion.div>
            <motion.div className="form-group" whileFocus={{ scale: 1.02 }}><input type="date" name="date" value={formData.date} onChange={handleInputChange} required /></motion.div>
            <motion.div className="form-group" whileFocus={{ scale: 1.02 }}><textarea name="message" placeholder="Your Message" value={formData.message} onChange={handleInputChange} rows="4"></textarea></motion.div>
            <motion.button type="submit" className="submit-btn" whileHover={{ scale: 1.02 }}>Send Inquiry <FaArrowRight /></motion.button>
          </form>
        </motion.div>
      </section>

      <footer className="footer">
        <motion.div className="footer-content" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}>
          <div className="footer-brand"><FaCar className="footer-logo" /><h3>LUXURY LEASING</h3><p>Excellence in every journey</p></div>
          <div className="footer-links"><h4>Quick Links</h4><a href="#fleet">Fleet</a><a href="#leasing">Leasing</a><a href="#chauffeur">Chauffeur</a><a href="#contact">Contact</a></div>
          <div className="footer-social"><h4>Follow Us</h4><motion.a href="#" whileHover={{ scale: 1.2, rotate: 360 }}>Instagram</motion.a><motion.a href="#" whileHover={{ scale: 1.2, rotate: 360 }}>Twitter</motion.a><motion.a href="#" whileHover={{ scale: 1.2, rotate: 360 }}>Facebook</motion.a></div>
        </motion.div>
        <div className="footer-bottom"><p>© 2024 LUXURY LEASING. All rights reserved.</p></div>
      </footer>

      {/* Floating Action Buttons */}
      <motion.div 
        className="scroll-top-btn"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: showScrollTop ? 1 : 0, x: 0 }}
        transition={{ duration: 0.3 }}
        style={{ display: showScrollTop ? 'flex' : 'none' }}
        onClick={scrollToTop}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <FaArrowUp />
      </motion.div>

      <motion.a 
        href="https://wa.me/26771234567" 
        className="whatsapp-btn"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <FaWhatsapp />
      </motion.a>

      {/* Search Overlay */}
      <AnimatePresence>
        {isSearchOpen && (
          <motion.div 
            className="search-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="search-container"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="search-header">
                <h2>Search Our Fleet</h2>
                <button className="close-search" onClick={closeSearch}><FaTimes /></button>
              </div>
              <div className="search-input-wrapper">
                <FaSearch className="search-input-icon" />
                <input 
                  type="text" 
                  placeholder="Search by car name or feature..." 
                  value={searchQuery}
                  onChange={handleSearch}
                  autoFocus
                />
              </div>
              {searchResults.length > 0 && (
                <div className="search-results">
                  <h3>Results ({searchResults.length})</h3>
                  <div className="results-grid">
                    {searchResults.map((car) => (
                      <div key={car.id} className="result-card">
                        <img src={car.image} alt={car.name} />
                        <div className="result-info">
                          <h4>{car.name}</h4>
                          <p className="result-price">{car.price} P/day</p>
                          <div className="result-features">
                            {car.features.slice(0, 2).map((f, i) => (
                              <span key={i} className="feature-tag">{f}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {searchQuery && searchResults.length === 0 && (
                <div className="no-results">
                  <p>No vehicles found matching "{searchQuery}"</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;

